/**
 * Which prompt rewrites a dictation. A stop applies one prompt at most:
 * the start/stop shortcut stops with Default (when it is switched on), and
 * Command+N (Windows+N) while listening stops with numbered prompt N, which
 * always rewrites — pressing it is the request. A blank instruction stops as
 * spoken.
 */
import type { DictationAnywherePrompt } from "./dictation-anywhere-native";
import type { DictationAnywhereSettings } from "@/stores/dictation-anywhere-settings-store";

export const DEFAULT_PROMPT_NAME = "Default";
/** Command+1 to Command+9. */
export const MAX_PROMPTS = 9;
export const PROMPT_NAME_MAX = 80;
export const PROMPT_TEXT_MAX = 4000;

type PromptSettings = Pick<
  DictationAnywhereSettings,
  "defaultPrompt" | "prompts"
>;

/** A numbered prompt chosen at stop time; `number` is its key, 1 to 9. */
export type NumberedPrompt = DictationAnywherePrompt & { number: number };

/** A numbered prompt's name as shown and saved in the history. */
export function promptDisplayName(
  prompt: { name: string },
  index: number,
): string {
  return prompt.name.trim() || `Prompt ${index + 1}`;
}

/** Numbered prompt `n` (1-based), or null when there is none. */
export function numberedPrompt(
  settings: PromptSettings,
  n: number,
): NumberedPrompt | null {
  const prompt = settings.prompts[n - 1];
  if (!prompt) return null;
  return {
    number: n,
    name: promptDisplayName(prompt, n - 1),
    instruction: prompt.instruction.trim(),
  };
}

/** Default as the model gets it, switched on or not; null when blank. */
function defaultPrompt(
  settings: PromptSettings,
): DictationAnywherePrompt | null {
  const instruction = settings.defaultPrompt.instruction.trim();
  return instruction ? { name: DEFAULT_PROMPT_NAME, instruction } : null;
}

/** The prompts a stop sends: the chosen one, else Default when it is on;
 *  empty to deliver the text as spoken. */
export function stopPrompts(
  settings: PromptSettings,
  chosen: NumberedPrompt | null,
): DictationAnywherePrompt[] {
  if (chosen) {
    return chosen.instruction
      ? [{ name: chosen.name, instruction: chosen.instruction }]
      : [];
  }
  const fallback = settings.defaultPrompt.enabled
    ? defaultPrompt(settings)
    : null;
  return fallback ? [fallback] : [];
}

/** Every prompt a stop of this recording could apply: what lets Rust start
 *  the AI runner early. */
export function rewriteCandidates(
  settings: PromptSettings,
): DictationAnywherePrompt[] {
  const numbered = settings.prompts.flatMap((_, index) => {
    const prompt = numberedPrompt(settings, index + 1)!;
    return prompt.instruction
      ? [{ name: prompt.name, instruction: prompt.instruction }]
      : [];
  });
  return [...stopPrompts(settings, null), ...numbered];
}

/** How many Command+N keys the flow claims while listening. */
export function promptKeyCount(settings: PromptSettings): number {
  return Math.min(settings.prompts.length, MAX_PROMPTS);
}

/**
 * The prompt a Retry from Recent dictations reruns: the numbered prompt the
 * dictation was stopped with, when one still has that name, else Default's
 * text even with its switch off — asking is the request. Null when there is
 * nothing to apply.
 */
export function retryPromptFor(
  settings: PromptSettings,
  promptNames: readonly string[],
): NumberedPrompt | DictationAnywherePrompt | null {
  const name = promptNames[0];
  if (name && name !== DEFAULT_PROMPT_NAME) {
    const index = settings.prompts.findIndex(
      (prompt, at) => promptDisplayName(prompt, at) === name,
    );
    const prompt = index >= 0 ? numberedPrompt(settings, index + 1) : null;
    if (prompt?.instruction) return prompt;
  }
  return defaultPrompt(settings);
}
